import type { InputHTMLAttributes } from 'react';
import { useId } from 'react';

type CampoProps = InputHTMLAttributes<HTMLInputElement> & {
  etiqueta: string;
  ayuda?: string | undefined;
  // `| undefined` explícito: con exactOptionalPropertyTypes, pasar `undefined`
  // desde el llamador no es lo mismo que omitir la prop, y acá se pasa así.
  error?: string | undefined;
};

/**
 * Campo de formulario con label real.
 *
 * El `label` está asociado por `htmlFor` y no es un placeholder disfrazado: un
 * placeholder desaparece al escribir, y quien vuelve al campo a corregir ya no
 * sabe qué le estaban pidiendo. Para un adulto mayor eso es la diferencia entre
 * completar el formulario y abandonarlo.
 *
 * El error se anuncia con `aria-describedby` y `role="alert"`, así que un lector
 * de pantalla lo lee cuando aparece y no cuando el usuario lo encuentra.
 */
export function Campo({ etiqueta, ayuda, error, className = '', ...resto }: CampoProps) {
  const id = useId();
  const idAyuda = `${id}-ayuda`;
  const idError = `${id}-error`;

  const descrito = [ayuda ? idAyuda : null, error ? idError : null].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-2">
      <label className="text-md font-semi text-heading" htmlFor={id}>
        {etiqueta}
      </label>

      {ayuda ? (
        <p className="text-sm text-muted-foreground" id={idAyuda}>
          {ayuda}
        </p>
      ) : null}

      <input
        aria-invalid={error ? true : undefined}
        className={`min-h-14 rounded-lg border-2 border-border bg-card px-4 text-lg text-foreground focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${error ? 'border-destructive' : ''} ${className}`}
        id={id}
        {...(descrito ? { 'aria-describedby': descrito } : {})}
        {...resto}
      />

      {error ? (
        <p className="text-sm font-semi text-destructive-fg" id={idError} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
